import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Filter, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import {
  UserReportFilter,
  UserReportDTO,
  UserStatus,
  SeniorityLevelEnum,
} from "@/types/reports";
import { ShirtSize } from "@/types/shirtSizes";
import { buildUserReportQueryString } from "@/utils/userReportUtils";
import {
  getCityOptions,
  getCityLabel,
  getCitiesByCounty,
  CityEnum,
} from "@/utils/cities";
import { BackLink } from "@/components/brand/BackLink";

// Position interface for dropdown
interface Position {
  id: string;
  tableName: string;
}

export default function UserReport() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<UserReportFilter>({});
  const [previewData, setPreviewData] = useState<UserReportDTO[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch positions for dropdown
  const { data: positions = [] } = useQuery({
    queryKey: ["positions"],
    queryFn: async (): Promise<Position[]> => {
      const response = await axiosInstance.get("position");
      return response.data;
    },
  });

  // Get city options - memoized since they're static
  const cityOptions = useMemo(() => getCityOptions(), []);
  const citiesByCounty = useMemo(() => getCitiesByCounty(), []);

  // Set default filters - select all options by default
  useEffect(() => {
    if (positions.length > 0 && Object.keys(filters).length === 0) {
      setFilters({
        userStatuses: [
          "PENDING_APPROVAL",
          "ACTIVE",
          "INACTIVE",
          "TERMINATED",
        ] as UserStatus[],
        seniorityLevels: [
          SeniorityLevelEnum.LEVEL_1,
          SeniorityLevelEnum.LEVEL_2,
          SeniorityLevelEnum.LEVEL_3,
          SeniorityLevelEnum.LEVEL_4,
          SeniorityLevelEnum.LEVEL_5,
        ],
        positionIds: positions.map((pos) => pos.id),
        shirtSizes: [
          ShirtSize.SMALL,
          ShirtSize.MEDIUM,
          ShirtSize.LARGE,
          ShirtSize.XLARGE,
          ShirtSize.XXLARGE,
          ShirtSize.XXXLARGE,
          ShirtSize.XXXXLARGE,
          ShirtSize.WOMENS_XSMALL,
          ShirtSize.WOMENS_SMALL,
          ShirtSize.WOMENS_MEDIUM,
          ShirtSize.WOMENS_LARGE,
          ShirtSize.WOMENS_XLARGE,
          ShirtSize.WOMENS_XXLARGE,
          ShirtSize.WOMENS_XXXLARGE,
        ],
        cities: cityOptions.map((city) => city.value),
      });
    }
  }, [positions, filters, cityOptions]);

  // Filter options
  const userStatusOptions = [
    { value: "PENDING_APPROVAL" as UserStatus, label: "Pending Approval" },
    { value: "ACTIVE" as UserStatus, label: "Active" },
    { value: "INACTIVE" as UserStatus, label: "Inactive" },
    { value: "TERMINATED" as UserStatus, label: "Terminated" },
  ];

  // Static seniority level options as per API documentation
  const seniorityLevelOptions = [
    { value: SeniorityLevelEnum.LEVEL_1, label: "Level 1" },
    { value: SeniorityLevelEnum.LEVEL_2, label: "Level 2" },
    { value: SeniorityLevelEnum.LEVEL_3, label: "Level 3" },
    { value: SeniorityLevelEnum.LEVEL_4, label: "Level 4" },
    { value: SeniorityLevelEnum.LEVEL_5, label: "Level 5" },
  ];

  const shirtSizeOptions = [
    { value: ShirtSize.SMALL, label: "Small" },
    { value: ShirtSize.MEDIUM, label: "Medium" },
    { value: ShirtSize.LARGE, label: "Large" },
    { value: ShirtSize.XLARGE, label: "X-Large" },
    { value: ShirtSize.XXLARGE, label: "XX-Large" },
    { value: ShirtSize.XXXLARGE, label: "XXX-Large" },
    { value: ShirtSize.XXXXLARGE, label: "XXXX-Large" },
    { value: ShirtSize.WOMENS_XSMALL, label: "Women's X-Small" },
    { value: ShirtSize.WOMENS_SMALL, label: "Women's Small" },
    { value: ShirtSize.WOMENS_MEDIUM, label: "Women's Medium" },
    { value: ShirtSize.WOMENS_LARGE, label: "Women's Large" },
    { value: ShirtSize.WOMENS_XLARGE, label: "Women's X-Large" },
    { value: ShirtSize.WOMENS_XXLARGE, label: "Women's XX-Large" },
    { value: ShirtSize.WOMENS_XXXLARGE, label: "Women's XXX-Large" },
  ];

  const booleanOptions = [
    { value: "all", label: "All" },
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];

  const handleArrayFilterChange = (
    filterKey: keyof UserReportFilter,
    value: string,
    checked: boolean,
  ) => {
    setFilters((prev) => {
      const currentArray = (prev[filterKey] as string[]) || [];
      const newArray = checked
        ? [...currentArray, value]
        : currentArray.filter((item) => item !== value);

      return {
        ...prev,
        [filterKey]: newArray.length > 0 ? newArray : undefined,
      };
    });
  };

  const handleSingleFilterChange = (
    filterKey: keyof UserReportFilter,
    value: string,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value === "" ? undefined : value,
    }));
  };

  const handleBooleanFilterChange = (
    filterKey: keyof UserReportFilter,
    value: string,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value === "all" ? undefined : value === "true",
    }));
  };

  // Helper functions to detect when "all" options are selected
  const isAllShirtSizesSelected = () => {
    return filters.shirtSizes?.length === shirtSizeOptions.length;
  };

  const isAllPositionsSelected = () => {
    return filters.positionIds?.length === positions.length;
  };

  const isAllCitiesSelected = () => {
    return filters.cities?.length === cityOptions.length;
  };

  // Preview report data
  const previewReport = async () => {
    setIsLoadingPreview(true);
    try {
      // Create modified filters that exclude shirt sizes, positions, and cities when "all" are selected
      const modifiedFilters = { ...filters };

      if (isAllShirtSizesSelected()) {
        delete modifiedFilters.shirtSizes; // Don't send shirt size filter if all are selected
      }

      if (isAllPositionsSelected()) {
        delete modifiedFilters.positionIds; // Don't send position filter if all are selected
      }

      if (isAllCitiesSelected()) {
        delete modifiedFilters.cities; // Don't send city filter if all are selected
      }

      const queryString = buildUserReportQueryString(modifiedFilters);
      const response = await axiosInstance.get(
        `/reports/users${queryString ? `?${queryString}` : ""}`,
      );
      setPreviewData(response.data);
      toast({
        title: "Success",
        description: `Loaded ${response.data.length} user records`,
        variant: "default",
      });
    } catch (error) {
      console.error("Preview failed:", error);
      toast({
        title: "Error",
        description: "Failed to load report data. Please try again.",
        variant: "destructive",
      });
      setPreviewData([]);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Download Excel report
  const downloadExcel = async () => {
    setIsDownloading(true);
    try {
      // Create modified filters that exclude shirt sizes, positions, and cities when "all" are selected
      const modifiedFilters = { ...filters };

      if (isAllShirtSizesSelected()) {
        delete modifiedFilters.shirtSizes; // Don't send shirt size filter if all are selected
      }

      if (isAllPositionsSelected()) {
        delete modifiedFilters.positionIds; // Don't send position filter if all are selected
      }

      if (isAllCitiesSelected()) {
        delete modifiedFilters.cities; // Don't send city filter if all are selected
      }

      const queryString = buildUserReportQueryString(modifiedFilters);
      const response = await axiosInstance.get(
        `/reports/users/export${queryString ? `?${queryString}` : ""}`,
        {
          responseType: "blob",
        },
      );

      const contentDisposition = response.headers["content-disposition"];
      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1].replace(/"/g, "")
        : "User_Report.xlsx";

      // Create download link
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: "Report downloaded successfully!",
        variant: "default",
      });
    } catch (error) {
      console.error("Download failed:", error);
      toast({
        title: "Error",
        description: "Failed to download report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const resetFilters = () => {
    setFilters({});
    setPreviewData([]);
  };

  const createPositionColumns = (userData: UserReportDTO[]) => {
    if (!userData.length) return [];

    const positions = new Set<string>();
    userData.forEach((user) => {
      Object.keys(user.positionEligibility || {}).forEach((pos) =>
        positions.add(pos),
      );
    });

    return Array.from(positions).sort();
  };

  const positionColumns = createPositionColumns(previewData);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <BackLink to="/admin/reports">Back to Reports</BackLink>
      </div>

      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-foreground">User Report</h1>
        <p className="text-foreground">
          Export comprehensive user data with advanced filtering options
        </p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
          <CardDescription>
            Apply filters to customize your user report
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nameSearch">Search Names</Label>
              <Input
                id="nameSearch"
                placeholder="Enter name to search..."
                value={filters.nameSearch || ""}
                onChange={(e) =>
                  handleSingleFilterChange("nameSearch", e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailSearch">Search Emails</Label>
              <Input
                id="emailSearch"
                placeholder="Enter email to search..."
                value={filters.emailSearch || ""}
                onChange={(e) =>
                  handleSingleFilterChange("emailSearch", e.target.value)
                }
              />
            </div>
          </div>

          {/* User Status Multi-Select */}
          <div className="space-y-2">
            <Label>User Status</Label>
            <div className="flex flex-wrap gap-2">
              {userStatusOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${option.value}`}
                    checked={
                      filters.userStatuses?.includes(option.value) || false
                    }
                    onCheckedChange={(checked) =>
                      handleArrayFilterChange(
                        "userStatuses",
                        option.value,
                        !!checked,
                      )
                    }
                  />
                  <Label htmlFor={`status-${option.value}`} className="text-sm">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Seniority Level Multi-Select */}
          <div className="space-y-2">
            <Label>Seniority Levels</Label>
            <div className="flex flex-wrap gap-2">
              {seniorityLevelOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`seniority-${option.value}`}
                    checked={
                      filters.seniorityLevels?.includes(option.value) || false
                    }
                    onCheckedChange={(checked) =>
                      handleArrayFilterChange(
                        "seniorityLevels",
                        option.value,
                        !!checked,
                      )
                    }
                  />
                  <Label
                    htmlFor={`seniority-${option.value}`}
                    className="text-sm"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Position Multi-Select */}
          <div className="space-y-2">
            <Label>Positions</Label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {positions.map((position) => (
                <div key={position.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`position-${position.id}`}
                    checked={
                      filters.positionIds?.includes(position.id) || false
                    }
                    onCheckedChange={(checked) =>
                      handleArrayFilterChange(
                        "positionIds",
                        position.id,
                        !!checked,
                      )
                    }
                  />
                  <Label
                    htmlFor={`position-${position.id}`}
                    className="text-sm"
                  >
                    {position.tableName}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Boolean Filters */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="taxDocs">Tax Documents Signed</Label>
              <Select
                value={filters.taxDocumentsSigned?.toString() || "all"}
                onValueChange={(value) =>
                  handleBooleanFilterChange("taxDocumentsSigned", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {booleanOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="badge">Badge Received</Label>
              <Select
                value={filters.badgeReceived?.toString() || "all"}
                onValueChange={(value) =>
                  handleBooleanFilterChange("badgeReceived", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {booleanOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shirt">Shirt Received</Label>
              <Select
                value={filters.receivedShirt?.toString() || "all"}
                onValueChange={(value) =>
                  handleBooleanFilterChange("receivedShirt", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {booleanOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Shirt Size Multi-Select */}
          <div className="space-y-2">
            <Label>Shirt Sizes</Label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {shirtSizeOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`shirt-${option.value}`}
                    checked={
                      filters.shirtSizes?.includes(option.value) || false
                    }
                    onCheckedChange={(checked) =>
                      handleArrayFilterChange(
                        "shirtSizes",
                        option.value,
                        !!checked,
                      )
                    }
                  />
                  <Label htmlFor={`shirt-${option.value}`} className="text-sm">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Bilingual Filter */}
          <div className="space-y-2">
            <Label htmlFor="bilingual">Bilingual</Label>
            <Select
              value={filters.bilingual?.toString() || "all"}
              onValueChange={(value) =>
                handleBooleanFilterChange("bilingual", value)
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                {booleanOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Cities Multi-Select - Grouped by County */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Cities ({filters.cities?.length || 0} selected)</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      cities: cityOptions.map((c) => c.value),
                    }))
                  }
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, cities: [] }))
                  }
                >
                  Clear All
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-h-48 overflow-y-auto border rounded-md p-3">
              {citiesByCounty.slice(0, 3).map(({ county, cities }) => (
                <div key={county} className="space-y-2">
                  <h4 className="font-medium text-sm text-foreground border-b pb-1">
                    {county}
                  </h4>
                  <div className="space-y-1 text-xs">
                    {cities.map((city) => (
                      <div
                        key={city.value}
                        className="flex items-center space-x-1"
                      >
                        <Checkbox
                          id={`city-${city.value}`}
                          checked={
                            filters.cities?.includes(city.value) || false
                          }
                          onCheckedChange={(checked) =>
                            handleArrayFilterChange(
                              "cities",
                              city.value,
                              !!checked,
                            )
                          }
                        />
                        <Label
                          htmlFor={`city-${city.value}`}
                          className="text-xs cursor-pointer"
                        >
                          {city.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Cache & Weber Counties in combined column */}
              <div className="space-y-4">
                {citiesByCounty.slice(3).map(({ county, cities }) => (
                  <div key={county} className="space-y-2">
                    <h4 className="font-medium text-sm text-foreground border-b pb-1">
                      {county}
                    </h4>
                    <div className="space-y-1 text-xs">
                      {cities.map((city) => (
                        <div
                          key={city.value}
                          className="flex items-center space-x-1"
                        >
                          <Checkbox
                            id={`city-${city.value}`}
                            checked={
                              filters.cities?.includes(city.value) || false
                            }
                            onCheckedChange={(checked) =>
                              handleArrayFilterChange(
                                "cities",
                                city.value,
                                !!checked,
                              )
                            }
                          />
                          <Label
                            htmlFor={`city-${city.value}`}
                            className="text-xs cursor-pointer"
                          >
                            {city.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 pt-4 border-t">
            <Button
              onClick={previewReport}
              disabled={isLoadingPreview}
              className="flex-1 md:flex-none"
            >
              {isLoadingPreview ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Filter className="h-4 w-4 mr-2" />
              )}
              Preview Report
            </Button>
            <Button
              onClick={downloadExcel}
              disabled={isDownloading}
              variant="outline"
              className="flex-1 md:flex-none"
            >
              {isDownloading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Excel
            </Button>
            <Button
              onClick={resetFilters}
              variant="ghost"
              className="flex-1 md:flex-none"
            >
              Reset Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Results */}
      {previewData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Preview Results</CardTitle>
            <CardDescription>
              Showing {previewData.length} user records
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Seniority</TableHead>
                    <TableHead>Tax Docs</TableHead>
                    <TableHead>Badge</TableHead>
                    <TableHead>Shirt</TableHead>
                    <TableHead>Shirt Size</TableHead>
                    {positionColumns.map((position) => (
                      <TableHead key={position}>{position}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.map((user, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {user.fullName}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phoneNumber}</TableCell>
                      <TableCell>
                        {user.city
                          ? getCityLabel(user.city as CityEnum)
                          : "Not specified"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.status}</Badge>
                      </TableCell>
                      <TableCell>{user.seniorityLevel}</TableCell>
                      <TableCell>
                        {user.taxDocumentsSigned ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>{user.badgeReceived ? "Yes" : "No"}</TableCell>
                      <TableCell>{user.receivedShirt ? "Yes" : "No"}</TableCell>
                      <TableCell>{user.shirtSize}</TableCell>
                      {positionColumns.map((position) => (
                        <TableCell key={position}>
                          {user.positionEligibility?.[position] ? "✓" : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data Message */}
      {!isLoadingPreview && previewData.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground py-8">
              No preview data. Click "Preview Report" to see filtered results.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
